import {
  Button,
  Group,
  Paper,
  Select,
} from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dashboardApi } from '../../data-access/api/dashboard.api';
import {
  DashboardClause,
  DEFAULT_GP_CAMPAIGN_YEARS,
  DEFAULT_NGP_CAMPAIGN_YEARS,
} from '../../data-access/types/stats';
import {
  useDashboardOrganisations,
  useOrganisationFilterState,
} from '../../hooks/use-dashboard-organisations';
import { FilterOption } from '../../utils/filter-options';

import { DashboardClauseCards } from './dashboard-clause-cards';
import { OrganisationFilterPopover } from './organisation-filter-popover';
import { OwnerFilterPopover } from './owner-filter-popover';

import { FORM_STATUS, useSharedQueries } from '@data-access';

export { DEFAULT_GP_CAMPAIGN_YEARS, DEFAULT_NGP_CAMPAIGN_YEARS };

interface StatisticsHeaderProps {
  organisationIds: number[];
  onOrganisationIdsChange: (ids: number[]) => void;
  ownerIds?: number[];
  onOwnerIdsChange?: (ids: number[]) => void;
  ownerOptions?: FilterOption[];
  showOwnerFilter?: boolean;
  clause: DashboardClause;
  onClauseChange: (clause: DashboardClause) => void;
  campaignYear: number;
  onCampaignYearChange: (year: number) => void;
  availableCampaignYears?: readonly number[];
  gpCampaignYears?: number[];
  onGpCampaignYearsChange?: (years: number[]) => void;
}

export function StatisticsHeader({
  organisationIds,
  onOrganisationIdsChange,
  ownerIds = [],
  onOwnerIdsChange,
  ownerOptions = [],
  showOwnerFilter = false,
  clause,
  onClauseChange,
  campaignYear,
  onCampaignYearChange,
  availableCampaignYears = DEFAULT_NGP_CAMPAIGN_YEARS,
  gpCampaignYears = [...DEFAULT_GP_CAMPAIGN_YEARS],
  onGpCampaignYearsChange,
}: StatisticsHeaderProps) {
  const { t } = useTranslation();
  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);
  const organisationsQuery = useDashboardOrganisations();
  const { defaultOrganisationIds, organisationOptions } =
    useOrganisationFilterState(organisationsQuery.data, clause);

  const exportStats = useMutation({
    mutationFn: (ids: number[]) => dashboardApi.exportStats(ids),
    onSuccess: (data) => {
      setComputationRequestId(data.instance_id);
    },
  });
  const downloadExport = useMutation({
    mutationFn: dashboardApi.downloadExport,
  });
  const sharedQueries = useSharedQueries();
  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId ?? -1 }),
    enabled: computationRequestId !== null,
  });

  useEffect(() => {
    if (computationRequest.data?.status === FORM_STATUS.FINISHED) {
      downloadExport.mutate({ id: computationRequest.data.id });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadExport]);

  const yearOptions = availableCampaignYears.map((year) => ({
    value: String(year),
    label: String(year),
  }));

  return (
    <Paper>
      <Group align="center" justify="space-between" wrap="wrap">
        <Group align="center" gap="md" wrap="wrap">
          <DashboardClauseCards onChange={onClauseChange} value={clause} />
          {clause === 'gp' && onGpCampaignYearsChange ? (
            <Group
              aria-label={t('dashboard.pratiques.gp-campaign-years.group_label', {
                defaultValue: 'Campagnes GP',
              })}
              gap="xs"
              role="group"
              wrap="wrap"
            >
              {DEFAULT_GP_CAMPAIGN_YEARS.map((year) => {
                const active = gpCampaignYears.includes(year);

                return (
                  <Button
                    aria-pressed={active}
                    key={year}
                    onClick={() => {
                      if (active) {
                        if (gpCampaignYears.length <= 1) {
                          return;
                        }
                        onGpCampaignYearsChange(
                          gpCampaignYears.filter((value) => value !== year),
                        );
                        return;
                      }
                      onGpCampaignYearsChange(
                        [...gpCampaignYears, year].sort((a, b) => a - b),
                      );
                    }}
                    type="button"
                    variant={active ? 'filled' : 'default'}
                  >
                    {year}
                  </Button>
                );
              })}
            </Group>
          ) : null}
          {clause === 'new' && (
            <Select
              allowDeselect={false}
              data={yearOptions}
              onChange={(value) => {
                if (value) {
                  onCampaignYearChange(Number(value));
                }
              }}
              value={String(campaignYear)}
              w={120}
            />
          )}
        </Group>
        <Group align="center" wrap="wrap">
          <OrganisationFilterPopover
            defaultOrganisationIds={defaultOrganisationIds}
            onOrganisationIdsChange={onOrganisationIdsChange}
            organisationIds={organisationIds}
            organisationOptions={organisationOptions}
          />
          {showOwnerFilter && onOwnerIdsChange && (
            <OwnerFilterPopover
              onOwnerIdsChange={onOwnerIdsChange}
              ownerIds={ownerIds}
              ownerOptions={ownerOptions}
            />
          )}
          <Button
            disabled={true}
            loading={computationRequest.data?.status === FORM_STATUS.PENDING}
            onClick={() => {
              exportStats.mutate(organisationIds);
            }}
          >
            {t('statistics.export')}
          </Button>
        </Group>
      </Group>
    </Paper>
  );
}

export default StatisticsHeader;
